import { components } from '@feedfm/clips-services-api';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { LazyThumbnail } from '../LazyThumbnail';
import {
  FEEDBACK_ACTION,
  type DisplayedFeedbackAction,
} from './SimilarTracksModal';
import * as styles from './SimilarTracksModal.styles';
import type { SimilarTrack } from './types';

interface TrackCardProps {
  track: SimilarTrack;
  index: number;
  isPlaying: boolean;
  previousFeedback?: { action: DisplayedFeedbackAction; createdAt: Date };
  onPlayPause: () => void;
  onThumbsUp: () => void;
  onThumbsDown: () => void;
  onVibeTagClick: (vibeTagId: number, vibeTagName: string) => void;
}

export const TrackCard = ({
  track,
  index,
  isPlaying,
  previousFeedback,
  onPlayPause,
  onThumbsUp,
  onThumbsDown,
  onVibeTagClick,
}: TrackCardProps) => {
  const similarityPercent = track.similarity * 100;

  return (
    <Box sx={styles.trackCard}>
      <Box
        sx={styles.trackCardContent(
          track.vibeTags && track.vibeTags.length > 0 ? 2 : 0,
        )}
      >
        {/* Rank badge - subtle and small */}
        <Box sx={styles.rankBadge}>{index + 1}.</Box>

        {/* Artwork with play button */}
        <Box sx={styles.artworkContainer}>
          <LazyThumbnail
            src={track.artworkThumbnailUrl}
            fallbackSrc={track.artworkPreSignedUrl}
            alt="Album cover"
            sx={styles.artworkImage}
            placeholder={
              <Box sx={styles.artworkPlaceholder}>
                <MusicNoteIcon sx={styles.artworkPlaceholderIcon} />
              </Box>
            }
          />

          {track.preSignedUrl && (
            <IconButton
              size="small"
              onClick={onPlayPause}
              sx={styles.playButton(isPlaying)}
            >
              {isPlaying ? (
                <PauseIcon sx={styles.playPauseIcon} />
              ) : (
                <PlayArrowIcon sx={styles.playPauseIcon} />
              )}
            </IconButton>
          )}
        </Box>

        {/* Track info */}
        <Box sx={styles.trackInfoContainer}>
          <Box sx={styles.trackInfoInner}>
            {/* Track title - bold and larger */}
            <Typography
              variant="body1"
              fontWeight={600}
              fontSize="1rem"
              noWrap
              sx={styles.trackTitle}
            >
              {track.title}
            </Typography>
            {/* Artist - lighter weight */}
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={400}
              noWrap
              sx={styles.trackArtist}
            >
              {track.displayArtistName}
            </Typography>
            {/* Match indicator - simpler */}
            <Typography
              variant="caption"
              sx={styles.similarityText(similarityPercent)}
            >
              {similarityPercent >= 85
                ? 'Strong Match'
                : similarityPercent >= 70
                  ? 'Good Match'
                  : 'Match'}{' '}
              <Box component="span" sx={styles.similarityPercent}>
                {similarityPercent.toFixed(0)}%
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Thumbs up/down - diagonal layout */}
        <Box sx={styles.thumbsContainer}>
          {/* Diagonal separator line */}
          <Box sx={styles.diagonalSeparator} />
          {/* Thumbs up - top left */}
          <Tooltip
            title={
              previousFeedback?.action === FEEDBACK_ACTION.THUMBS_UP
                ? 'You marked this as similar'
                : 'Mark as more similar'
            }
            arrow
            placement="left"
          >
            <IconButton
              onClick={onThumbsUp}
              sx={{
                ...styles.thumbsUpButton,
                ...(previousFeedback?.action === 'thumbs_up'
                  ? {
                      color: 'primary.main',
                      backgroundColor: 'primary.50',
                      borderColor: 'primary.main',
                    }
                  : {
                      color: 'text.secondary',
                      backgroundColor: 'background.paper',
                      borderColor: 'divider',
                    }),
              }}
              aria-label="Mark as more similar"
            >
              <ThumbUpIcon sx={styles.thumbsIcon} />
            </IconButton>
          </Tooltip>
          {/* Thumbs down - bottom right */}
          <Tooltip
            title={
              previousFeedback?.action === FEEDBACK_ACTION.THUMBS_DOWN
                ? 'You marked this as not similar'
                : 'Mark as less similar'
            }
            arrow
            placement="right"
          >
            <IconButton
              onClick={onThumbsDown}
              sx={{
                ...styles.thumbsDownButton,
                ...(previousFeedback?.action === 'thumbs_down'
                  ? {
                      color: 'error.main',
                      backgroundColor: 'error.50',
                      borderColor: 'error.main',
                    }
                  : {
                      color: 'text.secondary',
                      backgroundColor: 'background.paper',
                      borderColor: 'divider',
                    }),
              }}
              aria-label="Mark as less similar"
            >
              <ThumbDownIcon sx={styles.thumbsIcon} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Shared vibes - simpler and clickable */}
      {track.vibeTags && track.vibeTags.length > 0 && (
        <Box sx={styles.vibeTagsContainer}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={styles.vibeTagsLabel}
          >
            These tracks share these vibes:
          </Typography>
          <Box sx={styles.vibeTagsList}>
            {track.vibeTags.map((tag) => (
              <Chip
                key={tag.vibeTagId}
                label={tag.vibeTag}
                size="small"
                variant="outlined"
                onClick={() => onVibeTagClick(tag.vibeTagId, tag.vibeTag)}
                sx={styles.vibeTagChip}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Helper to create a single-clip collection for playback
export const createSingleClipCollection = (
  track: SimilarTrack,
): components['schemas']['Collection'] => {
  const clipId = track.clipId ?? track.trackEmbeddingId;
  return {
    collectionId: clipId,
    collectionName: track.title,
    description: `Single clip: ${track.title}`,
    publishedOn: null,
    rating: 'unknown',
    explicit: false,
    clips: [
      {
        clipId: clipId,
        trackId: 0,
        title: track.title,
        artist: track.displayArtistName,
        album: '',
        duration: 0,
        preSignedUrl: track.preSignedUrl!,
        preSignedUrlExpires: '',
        encoding: '',
        bitrate: 0,
        explicitness: 'unknown',
        artworkPreSignedUrl: track.artworkPreSignedUrl ?? undefined,
        artworkThumbnailUrl: track.artworkThumbnailUrl ?? null,
        createDate: new Date().toISOString(),
      },
    ],
  };
};
